import React, { useState, useRef, useEffect } from 'react';

interface AssetClass {
  name: string;
  longTermReturn: number;
  volatility: number;
  description: string;
  minReturn: number;
  maxReturn: number;
  minVolatility: number;
  maxVolatility: number;
}

interface Props {
  assetClasses: AssetClass[];
  onUpdate: (updatedAssetClasses: AssetClass[]) => void;
}

const CapitalMarketAssumptions: React.FC<Props> = ({ assetClasses, onUpdate }) => {
  const [showInfo, setShowInfo] = useState(false);
  const [showDescription, setShowDescription] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const timeoutRef = useRef<number>();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (index: number, field: 'longTermReturn' | 'volatility', value: string) => {
    const updatedAssetClasses = [...assetClasses];
    updatedAssetClasses[index][field] = parseFloat(value) || 0;
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set new timeout to update after 300ms of no changes
    timeoutRef.current = window.setTimeout(() => {
      onUpdate(updatedAssetClasses);
    }, 300);
  };

  return (
    <div className="capital-market-assumptions" style={{ marginBottom: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>Capital Market Assumptions</h2>
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            padding: '8px 16px',
            backgroundColor: '#1e1e1e',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {isExpanded ? 'Hide Assumptions' : 'Show Assumptions'}
        </button>
      </div>

      {isExpanded && (
        <div>
          <button 
            onClick={() => setShowInfo(!showInfo)}
            style={{
              padding: '8px 16px',
              marginTop: '10px',
              marginBottom: '20px',
              backgroundColor: '#1e1e1e',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {showInfo ? 'Hide Info' : 'What are Capital Market Assumptions?'}
          </button>

          {showInfo && (
            <div className="info-box" style={{
              backgroundColor: '#f5f5f5',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h3>Capital Market Assumptions</h3>
              <p>
                Capital Market Assumptions (CMAs) are forward-looking estimates of risk and return for various asset classes. 
                They are crucial for strategic asset allocation and long-term investment planning.
              </p>
              <h4>Why are CMAs important?</h4>
              <ul>
                <li>Guide investment decisions and portfolio construction</li>
                <li>Help set realistic expectations for future returns</li>
                <li>Assist in risk management and diversification strategies</li>
                <li>Enable more accurate financial planning and goal setting</li>
              </ul>
              <p>
                By adjusting these assumptions, you can see how different market expectations might impact your strategic asset allocation.
                Remember, these are long-term projections and actual short-term performance may vary significantly.
              </p>
            </div>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Asset Class</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Long-Term Return (%)</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Volatility (%)</th>
              </tr>
            </thead>
            <tbody>
              {assetClasses.map((assetClass, index) => (
                <tr 
                  key={assetClass.name} 
                  style={{ borderBottom: '1px solid #ddd' }}
                >
                  <td style={{ padding: '12px', position: 'relative', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {assetClass.name}
                    <div 
                      style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        border: '1px solid #ccc',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: '#666'
                      }}
                      onClick={() => setShowDescription(showDescription === assetClass.name ? null : assetClass.name)}
                    >
                      ?
                    </div>
                    {showDescription === assetClass.name && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: '0',
                        backgroundColor: 'white',
                        padding: '10px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        zIndex: 1000,
                        width: '200px'
                      }}>
                        {assetClass.description}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                        <input
                          type="range"
                          min={assetClass.minReturn}
                          max={assetClass.maxReturn}
                          step="0.1"
                          value={assetClass.longTermReturn}
                          onChange={(e) => handleInputChange(index, 'longTermReturn', e.target.value)}
                          style={{ flexGrow: 1 }}
                        />
                        <span style={{ minWidth: '60px', textAlign: 'right' }}>{assetClass.longTermReturn.toFixed(1)}%</span>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                        <input
                          type="range"
                          min={assetClass.minVolatility}
                          max={assetClass.maxVolatility}
                          step="0.1"
                          value={assetClass.volatility}
                          onChange={(e) => handleInputChange(index, 'volatility', e.target.value)}
                          style={{ flexGrow: 1 }}
                        />
                        <span style={{ minWidth: '60px', textAlign: 'right' }}>{assetClass.volatility.toFixed(1)}%</span>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CapitalMarketAssumptions;
